import { TaskViewModel } from "../../../tasks/api/task"
import { DragUpdate } from "react-beautiful-dnd"
import { isCompleted, isNotStarted, isRejected } from "../../../utils/status"
import { BoardViewModelV2 } from "../../api/v2/board-view-model"

type MoveCardData = {
  boardId: string
  cardId: string
  columnId: string
  destColumnId: string
  position: number
}

type DragLike = Pick<DragUpdate, "source" | "destination" | "draggableId">
type Reason = "movingTaskToCompleted" | "movingTaskToNotStarted"

type MoveIntent =
  | {
      cardType: "todo"
      mayRequireConfirmation: false
    }
  | {
      cardType: "task"
      mayRequireConfirmation: false
    }
  | {
      cardType: "task"
      mayRequireConfirmation: true
      confirmationReason: Reason
    }

const MAX_POSITION_FOR_NOT_STARTED = 2
const COMPLETED_POSITION = 8

const getMoveConfirmationReason = (
  sourceColumnPosition: number,
  destColumnPosition: number
): "movingTaskToCompleted" | "movingTaskToNotStarted" | null => {
  // Moving into Completed (8) from outside
  if (destColumnPosition === COMPLETED_POSITION && sourceColumnPosition !== COMPLETED_POSITION) {
    return "movingTaskToCompleted"
  }

  // Moving into Not Started (0,1,2) from outside that zone
  const sourceIsNotStarted = sourceColumnPosition <= MAX_POSITION_FOR_NOT_STARTED
  const destIsNotStarted = destColumnPosition <= MAX_POSITION_FOR_NOT_STARTED

  if (destIsNotStarted && !sourceIsNotStarted) {
    return "movingTaskToNotStarted"
  }

  return null
}

export const resolveMoveIntent = (board: BoardViewModelV2, drag: DragLike): (MoveIntent & MoveCardData) | null => {
  const { draggableId, source, destination } = drag
  if (!draggableId || !source || !destination?.droppableId) return null

  const sourceColumn = board.columnsById[source.droppableId]
  const destColumn = board.columnsById[destination.droppableId]
  if (!sourceColumn || !destColumn) return null

  const card = sourceColumn.cards.find((c) => c.id === draggableId)
  if (!card) return null

  const moveData = {
    boardId: board.id,
    cardId: draggableId,
    columnId: source.droppableId,
    destColumnId: destination.droppableId,
    position: destination.index,
  }

  if (card.type === "todo") {
    return {
      cardType: "todo",
      mayRequireConfirmation: false,
      ...moveData,
    }
  }

  const confirmationReason = getMoveConfirmationReason(sourceColumn.position, destColumn.position)

  if (!confirmationReason) {
    return {
      cardType: "task",
      mayRequireConfirmation: false,
      ...moveData,
    }
  }

  return {
    cardType: "task",
    mayRequireConfirmation: true,
    confirmationReason,
    ...moveData,
  }
}

export const requiresConfirmation = (taskId: string, reason: Reason, subTasks: TaskViewModel[]) => {
  for (const task of subTasks) {
    if (task.archived) continue // archived tasks don't block moving parent task
    if (isRejected(task.status)) continue // rejected tasks don't block moving parent task
    if (task.id === taskId) continue // don't compare the task to itself

    if (reason === "movingTaskToCompleted" && !isCompleted(task.status)) {
      return true
    }

    if (reason === "movingTaskToNotStarted" && !isNotStarted(task.status)) {
      return true
    }
  }
}
